import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Observable } from 'rxjs';
import { TranslateService } from '@jht/brand-translate';

import { Config } from '../../../shared/config';
import { Topic } from './topic.model';
import { WINDOW } from '../../models/tokens';

declare interface Window {
    ga: any;
    fbq: any;
    location: any;
}

@Injectable()
export class FormSubmissionService {
    topicKey!: string;

    private _currentTopic = signal<Topic>({
        key: '',
        value: '',
        topicCategory: '',
    });
    currentTopic = this._currentTopic.asReadonly();

    url!: string;

    private config = inject(Config);
    private window = inject(WINDOW);
    private http = inject(HttpClient);
    private translateService = inject(TranslateService);
    private router = inject(Router);
    private platformId = inject(PLATFORM_ID);

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.url = this.router.url;
        }
    }

    setCurrentTopic(topic: Topic) {
        this._currentTopic.set(topic);
    }

    sendAnalytics() {
        if (this.window.ga) {
            this.window.ga('send', {
                hitType: 'event',
                eventCategory: `${this.config.getSbu()}: Form Submission`,
                eventAction: `clicked from Contact Us Form`,
                eventLabel: `URL: ${this.window.location.href}`,
            });
        }
        if (this.window.fbq) {
            this.window.fbq('track', 'Click', {
                hitType: 'event',
                eventCategory: `${this.config.getSbu()}: Form Submission`,
                eventAction: `clicked from Contact Us Form`,
                eventLabel: `URL: ${this.window.location.href}`,
            });
        }
    }

    sendToThankYouPage() {
        this.router.navigate(['/contact-us/thank-you']);
    }

    structureFormData(formData: FormData): FormData {
        const newFormData: FormData = new FormData();
        for (const [key, value] of formData.entries()) {
            if (typeof value !== 'object') {
                // Convert null or 'null' string values to empty strings
                const processedValue =
                    value === null || value === 'null' ? '' : value;
                newFormData.append(key, processedValue);
            } else if (key.indexOf('attachment') !== 0) {
                newFormData.append(key, value);
            } else if (
                key.indexOf('attachment') === 0 &&
                value instanceof File
            ) {
                newFormData.append('attachments[]', value);
            }
        }

        return newFormData;
    }

    setCustomFormFields(formData: FormData): FormData {
        if (!formData.get('_cc')) {
            const ccAddressKey = 'CONTACT-US_CC-ADDRESSES_EMAIL';
            const ccAddressKeyTranslated =
                this.translateService.instant(ccAddressKey);
            if (ccAddressKey !== ccAddressKeyTranslated) {
                formData.set('_cc', ccAddressKeyTranslated);
            }
        }

        if (!formData.get('_from')) {
            const fromAddressKey = 'CONTACT-US_DEFAULT-FROM_EMAIL';
            const fromAddressKeyTranslated =
                this.translateService.instant(fromAddressKey);
            if (fromAddressKey !== fromAddressKeyTranslated) {
                formData.set('_from', fromAddressKeyTranslated);
            }
        }
        return formData;
    }

    submitContactUsForm(formData: FormData): Observable<any> {
        formData = this.structureFormData(formData);
        formData = this.setCustomFormFields(formData);
        return this.http.post('api/v2/forms', formData, {
            headers: { refererpath: this.url },
        });
    }
}
